import {
  DEFAULT_LOCALE,
  interpolate,
  isLocale,
  LABELS,
  type LabelSets,
  LOCALE_TAGS,
  type Locale,
} from '@hm/contracts'
import { createContext, type ReactNode, use, useCallback, useEffect, useState } from 'react'
import { CATALOGS, type Dictionary, en, type MessageKey } from '~/i18n'

/**
 * Language, on the client.
 *
 * The shape follows `theme.tsx` deliberately — a context, a localStorage key, one
 * effect that touches the document — because it is the same kind of thing and a
 * second pattern for it would be a second thing to learn.
 *
 * Where it differs is that language is a property of the *person*, not the device.
 * A shop owner who sets the app to Nepali on their phone means it on the counter
 * machine too. So the choice is written to the account as well, and a device that
 * has never been told anything adopts whatever the account already says. Local
 * storage still comes first, because the sign-in screen has no session to ask and
 * because switching should take effect before a round trip, not after it.
 */

const STORAGE_KEY = 'hm-locale'

function readStored(): Locale | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return isLocale(stored) ? stored : null
  } catch {
    // Private mode or a blocked origin. The browser's own preference will do.
    return null
  }
}

function fromBrowser(): Locale {
  const tags = navigator.languages ?? [navigator.language]
  for (const tag of tags) {
    const base = tag?.toLowerCase().split('-')[0]
    if (base === 'ne') return 'ne'
    if (base === 'en') return 'en'
  }
  return DEFAULT_LOCALE
}

function apply(locale: Locale): void {
  document.documentElement.lang = LOCALE_TAGS[locale]
}

/**
 * Start fetching the catalog before React has mounted, rather than from an effect
 * after the first render.
 *
 * The Nepali catalog is a lazy chunk (see the budget in docs/02-architecture.md §7),
 * so somebody set to Nepali reads English for however long that round trip takes.
 * Asking for it at module scope puts it in flight alongside the session call
 * instead of behind the first paint; the provider's own request resolves off the
 * same cached module promise. It does not remove the gap on a cold cache, it
 * removes the part of it that was our own ordering.
 */
const preferred = readStored() ?? fromBrowser()
if (preferred !== DEFAULT_LOCALE) void CATALOGS[preferred]().catch(() => {})

interface LocaleValue {
  locale: Locale
  /** The catalog actually in memory, which lags `locale` for one fetch. */
  words: Dictionary
  /** Switch the language on screen. Persisting it to the account is the caller's job. */
  setLocale: (locale: Locale) => void
  /** True once somebody has chosen on this device, which is what makes it win. */
  chosenHere: boolean
}

const LocaleContext = createContext<LocaleValue | null>(null)

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setStored] = useState<Locale>(preferred)
  const [chosenHere, setChosenHere] = useState(() => readStored() !== null)
  const [dictionary, setDictionary] = useState<{ locale: Locale; words: Dictionary }>({
    locale: 'en',
    words: en,
  })

  useEffect(() => {
    apply(locale)
    if (dictionary.locale === locale) return
    let live = true
    CATALOGS[locale]()
      .then((words) => {
        if (!live) return
        current = words
        setDictionary({ locale, words })
      })
      .catch(() => {
        // A catalog that will not load leaves the screen in English, which is
        // readable. Failing to render at all would not be.
      })
    return () => {
      live = false
    }
  }, [locale, dictionary.locale])

  const setLocale = useCallback((next: Locale) => {
    try {
      localStorage.setItem(STORAGE_KEY, next)
      setChosenHere(true)
    } catch {
      // Not being able to remember the choice is not a reason to ignore it.
    }
    setStored(next)
  }, [])

  return (
    <LocaleContext value={{ locale, words: dictionary.words, setLocale, chosenHere }}>
      {children}
    </LocaleContext>
  )
}

function useLocaleContext(): LocaleValue {
  const value = use(LocaleContext)
  if (!value) throw new Error('useLocaleChoice used outside LocaleProvider')
  return value
}

/**
 * The language on screen.
 *
 * Falls back to `<html lang>` rather than throwing when there is no provider above
 * it, because this is called from leaf components — a price, a quantity chip — and
 * a hook that refuses to render outside the whole app makes those untestable on
 * their own. The attribute is the same source of truth the provider writes to, so
 * the fallback cannot disagree with it; it is only ever reached in a test or a
 * fragment rendered in isolation.
 */
export function useLocale(): Locale {
  const value = use(LocaleContext)
  if (value) return value.locale
  const tag = globalThis.document?.documentElement.lang?.split('-')[0]
  return isLocale(tag) ? tag : DEFAULT_LOCALE
}

export type TranslateParams = Readonly<Record<string, string | number>>

/**
 * `t('cart.empty')`, and `t('cart.lines', { count })` where the number changes the
 * sentence. A key ending `_one` is picked automatically when `count` is 1, which
 * covers every plural in the app — see the note on `plural` in contracts.
 */
export type Translate = (key: MessageKey, params?: TranslateParams) => string

function translator(dictionary: Dictionary): Translate {
  return (key, params) => {
    const plural = params?.count === 1 ? (`${key}_one` as MessageKey) : null
    const template = (plural && dictionary[plural]) || dictionary[key]
    return interpolate(template, params)
  }
}

/**
 * The same lookup for the code that has no component to hang a hook on — the fetch
 * wrapper's fallback message, the push helpers' refusals. The provider keeps this
 * in step; before it has mounted, or in a test, it is English, which is the
 * fallback everywhere else too.
 */
let current: Dictionary = en
export const translate: Translate = (key, params) => translator(current)(key, params)

export function useT(): Translate {
  const value = use(LocaleContext)
  // Outside the provider — a leaf component under test — English is the answer.
  return translator(value?.words ?? en)
}

/** The enum labels — order statuses, roles, audit actions — in the current language. */
export function useLabels(): LabelSets {
  return LABELS[useLocale()]
}

/**
 * The switcher's own state, plus persistence to the account when there is one.
 *
 * `onPersist` is passed in rather than imported so this hook stays usable on the
 * sign-in screen, where there is no session and nothing to persist to.
 */
export function useLocaleChoice(onPersist?: (locale: Locale) => void): {
  locale: Locale
  choose: (locale: Locale) => void
} {
  const { locale, setLocale } = useLocaleContext()
  return {
    locale,
    choose: (next) => {
      setLocale(next)
      onPersist?.(next)
    },
  }
}

/**
 * Reconcile the device and the account once the session arrives.
 *
 * It goes both ways, and which way depends on whether anybody has chosen on this
 * device. A device that has never been told a language adopts the account's, so
 * signing in on a new phone does not quietly ignore a preference set somewhere
 * else. A device where somebody *has* chosen pushes that up instead — the sign-in
 * screen has no session to persist to, so picking नेपाली there and then signing in
 * has to be what sets the account, or the choice would evaporate at the door.
 */
export function useSyncAccountLocale(
  accountLocale: Locale | undefined,
  remember: (locale: Locale) => void,
): void {
  const { locale, setLocale, chosenHere } = useLocaleContext()
  useEffect(() => {
    if (!accountLocale || accountLocale === locale) return
    if (chosenHere) remember(locale)
    else setLocale(accountLocale)
  }, [accountLocale, chosenHere, locale, setLocale, remember])
}
